import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import Modal from "../../UI/Modal";
import Loader from "../../UI/Loader";
import Alert from "../../UI/Alert";
import Select2 from "../../UI/Select2";
import {
  closeFormModal,
  openViewModal,
} from "../../../store/reducers/frasesConfirmacion/Frasesconfirmacionplantel.reducer";
import {
  createMultipleFrasesConfirmacionPlantelThunk,
  updateFraseConfirmacionPlantelThunk,
  getFrasesConfirmacionPlantelThunk,
  getFraseConfirmacionPlantelByIdThunk,
} from "../../../store/reducers/frasesConfirmacion/Frasesconfirmacionplantel.thunks";
import { getPreguntasSelectoresPlantelService, getTalentosSelectoresPlantelService } from "../../../services/planteles/talentosPlantel.service";

const FraseConfirmacionFormModalPlantel = () => {
  const dispatch = useDispatch();
  const { plantelId } = useParams();

  const {
    formModalOpen,
    formMode,
    selectedFrase,
    formLoading,
    formError,
    page,
    search,
    talentoFilter,
    preguntaFilter,
  } = useSelector((state) => state.frasesConfirmacionPlantel);

  // Estado para edición (una sola frase)
  const [editFormData, setEditFormData] = useState({
    frase: "",
    orden: "",
    talento: null,
    pregunta: null,
  });

  // Estado para creación múltiple
  const [createFormData, setCreateFormData] = useState({
    talento: null,
    pregunta: null,
    frases: [{ frase: "" }],
  });

  useEffect(() => {
    if (formMode === "edit" && selectedFrase) {
      setEditFormData({
        frase: selectedFrase.frase || "",
        orden: selectedFrase.orden || "",
        talento: selectedFrase.talento || null,
        pregunta: selectedFrase.pregunta || null,
      });
    } else {
      setCreateFormData({
        talento: null,
        pregunta: null,
        frases: [{ frase: "" }],
      });
    }
  }, [formMode, selectedFrase, formModalOpen]);

  const fetchTalentos = async (searchTerm) => {
    const response = await getTalentosSelectoresPlantelService(searchTerm);
    return response.map((item) => ({ id: item.id, label: item.label }));
  };

  const fetchPreguntas = async (searchTerm) => {
    const response = await getPreguntasSelectoresPlantelService(searchTerm);
    return response.map((item) => ({ id: item.id, label: item.label }));
  };

  // Handlers edición
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handlers creación múltiple
  const handleFraseChange = (index, value) => {
    setCreateFormData((prev) => {
      const newFrases = [...prev.frases];
      newFrases[index] = { frase: value };
      return { ...prev, frases: newFrases };
    });
  };

  const handleAddFrase = () => {
    setCreateFormData((prev) => ({
      ...prev,
      frases: [...prev.frases, { frase: "" }],
    }));
  };

  const handleRemoveFrase = (index) => {
    setCreateFormData((prev) => ({
      ...prev,
      frases: prev.frases.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formMode === "create") {
      const validFrases = createFormData.frases.filter(
        (f) => f.frase.trim() !== "",
      );
      if (validFrases.length === 0) {
        toast.error("Debes agregar al menos una frase");
        return;
      }

      const payload = {
        talento: createFormData.talento,
        pregunta: createFormData.pregunta,
        plantel: plantelId,
        frases: validFrases,
      };

      const result = await dispatch(
        createMultipleFrasesConfirmacionPlantelThunk(payload),
      );

      if (result.meta.requestStatus === "fulfilled") {
        dispatch(
          getFrasesConfirmacionPlantelThunk({
            plantelId,
            page: 1,
            search: "",
            talento: "",
            pregunta: "",
          }),
        );
        toast.success("Frases creadas exitosamente");
        dispatch(closeFormModal());
      }
    } else {
      // Edición
      const result = await dispatch(
        updateFraseConfirmacionPlantelThunk({
          id: selectedFrase.id,
          payload: editFormData,
        }),
      );

      if (result.meta.requestStatus === "fulfilled") {
        dispatch(
          getFrasesConfirmacionPlantelThunk({
            plantelId,
            page,
            search,
            talento: talentoFilter,
            pregunta: preguntaFilter,
          }),
        );
        toast.success("Frase actualizada exitosamente");
        await dispatch(getFraseConfirmacionPlantelByIdThunk(selectedFrase.id));
        dispatch(openViewModal());
      }
    }
  };

  return (
    <Modal
      open={formModalOpen}
      onClose={() => dispatch(closeFormModal())}
      title={formMode === "create" ? "Crear nuevas frases" : "Editar frase"}
      size="lg"
    >
      {formLoading ? (
        <Loader
          center
          type="dots"
          text={
            formMode === "create"
              ? "Creando frases..."
              : "Actualizando frase..."
          }
        />
      ) : (
        <form onSubmit={handleSubmit}>
          {formError && <Alert message={formError} variant="error" />}

          {formMode === "edit" ? (
            // ── EDICIÓN ────────────────────────────────────────────────────
            <>
              <div className="mb-3">
                <label className="form-label fw-medium">
                  Talento <span className="text-danger">*</span>
                </label>
                <Select2
                  value={editFormData.talento}
                  onChange={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      talento: value || null,
                    }))
                  }
                  fetchOptions={fetchTalentos}
                  placeholder="Selecciona un talento"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Pregunta <span className="text-danger">*</span>
                </label>
                <Select2
                  value={editFormData.pregunta}
                  onChange={(value) =>
                    setEditFormData((prev) => ({
                      ...prev,
                      pregunta: value || null,
                    }))
                  }
                  fetchOptions={fetchPreguntas}
                  placeholder="Selecciona una pregunta"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Frase <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  name="frase"
                  value={editFormData.frase}
                  onChange={handleEditChange}
                  placeholder="Ingrese la frase"
                  rows="4"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Orden</label>
                <input
                  type="number"
                  className="form-control"
                  name="orden"
                  value={editFormData.orden}
                  onChange={handleEditChange}
                  placeholder="Ingrese el orden (opcional)"
                />
              </div>
            </>
          ) : (
            // ── CREACIÓN MÚLTIPLE ──────────────────────────────────────────
            <>
              <div className="mb-3">
                <label className="form-label fw-medium">
                  Talento <span className="text-danger">*</span>
                </label>
                <Select2
                  value={createFormData.talento}
                  onChange={(value) =>
                    setCreateFormData((prev) => ({
                      ...prev,
                      talento: value || null,
                    }))
                  }
                  fetchOptions={fetchTalentos}
                  placeholder="Selecciona un talento"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">
                  Pregunta <span className="text-danger">*</span>
                </label>
                <Select2
                  value={createFormData.pregunta}
                  onChange={(value) =>
                    setCreateFormData((prev) => ({
                      ...prev,
                      pregunta: value || null,
                    }))
                  }
                  fetchOptions={fetchPreguntas}
                  placeholder="Selecciona una pregunta"
                  required
                />
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-medium mb-0">
                    Frases <span className="text-danger">*</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                    onClick={handleAddFrase}
                  >
                    <Icon icon="mdi:plus" width="16" />
                    Agregar frase
                  </button>
                </div>

                {createFormData.frases.map((item, index) => (
                  <div key={index} className="d-flex gap-2 mb-2">
                    <textarea
                      className="form-control"
                      value={item.frase}
                      onChange={(e) => handleFraseChange(index, e.target.value)}
                      placeholder={`Frase ${index + 1}`}
                      rows="2"
                    />
                    {createFormData.frases.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveFrase(index)}
                        style={{ minWidth: "40px" }}
                      >
                        <Icon icon="mdi:delete-outline" width="18" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => dispatch(closeFormModal())}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {formMode === "create" ? "Crear frases" : "Guardar cambios"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default FraseConfirmacionFormModalPlantel;
